import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { useMembership } from "../lib/membership";

/**
 * Once-per-session interstitial ad.
 *
 * Behaviour
 *   • Fires ONCE on initial app launch per browser tab session (persisted
 *     in sessionStorage under `proximity_ad_shown`).
 *   • Auto-opens immediately when the component first mounts for a
 *     logged-in, ad-eligible user.
 *   • Displays the "Proximity by Aaron Moore" banner artwork.
 *   • Shows a slim 3-second countdown progress bar across the top.
 *   • Auto-closes and unmounts after exactly 3 seconds — no manual close
 *     button, no user action required — dropping the user back exactly
 *     where they were.
 *
 * Suppressed for paying tiers (Plus / Elite) and node accounts.
 */
const SESSION_KEY = "proximity_ad_shown";
const DURATION_MS = 3000;

export default function InterstitialAd() {
  const { user } = useAuth();
  const { tier, isNode } = useMembership();
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(100); // 100 → 0

  const adsSuppressed = isNode || tier === "plus" || tier === "elite";

  useEffect(() => {
    if (!user || adsSuppressed) return;
    if (sessionStorage.getItem(SESSION_KEY)) return; // already shown this tab-session
    sessionStorage.setItem(SESSION_KEY, "1");
    setOpen(true);
  }, [user, adsSuppressed]);

  useEffect(() => {
    if (!open) return;
    const startedAt = Date.now();
    // ~60 fps progress bar for smooth animation
    const tick = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const pct = Math.max(0, 100 - (elapsed / DURATION_MS) * 100);
      setProgress(pct);
    }, 16);
    const close = setTimeout(() => setOpen(false), DURATION_MS);
    return () => { clearInterval(tick); clearTimeout(close); };
  }, [open]);

  if (adsSuppressed || !open) return null;

  return (
    <div
      data-testid="ad-interstitial"
      className="fixed inset-0 z-[100] bg-black grid place-items-center px-6 animate-in fade-in duration-300"
    >
      <div className="relative w-full max-w-md">
        {/* Slim countdown progress bar */}
        <div className="h-1 rounded-full bg-white/5 overflow-hidden mb-6" aria-hidden>
          <div
            data-testid="ad-progress-bar"
            className="h-full bg-[#007AFF] transition-[width] ease-linear"
            style={{ width: `${progress}%`, transitionDuration: "16ms" }}
          />
        </div>

        {/* "Proximity by Aaron Moore" banner artwork */}
        <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
          <img
            src="/ad-aaron-moore.png"
            alt="Proximity by Aaron Moore"
            className="w-full h-auto block select-none"
            draggable={false}
            data-testid="ad-image"
          />
        </div>
      </div>
    </div>
  );
}
